import React from 'react';
import styles from './ccf-lyra-sidebar.module.scss';
import { Drawer, Tooltip, useMediaQuery, useTheme, Theme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Navigation } from '../../enums/navigation-menus';
import { getContactsCount } from '../ccf-inbox/ccf-inbox.slice';
import {
  getQuickAppNavigationItems,
  getSelectedMenuName,
  globalActions,
} from '../global.app.slice';
import {
  NavigationItem,
  useNavigationItems,
} from '../ccf-navigation/useNavigationItems';
import {
  CcfAppHamburgerMenu,
} from '@nice-ccf/ui-components';
import {
  useTranslator,
} from '@nice-ccf/ui-controls';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

/* eslint-disable-next-line */
export interface CcfLyraSidebarProps {}

/**
 * Lyra design sidebar navigation component
 * @param props - CcfLyraSidebarProps
 * @returns Lyra sidebar component
 * @example <CcfLyraSidebar />
 */
export function CcfLyraSidebar(props: CcfLyraSidebarProps) {
  const inboxCount = useSelector(getContactsCount);
  const quickAppNavigationItemsLS = useSelector(getQuickAppNavigationItems);
  const { setSelectedMenu } = globalActions;
  const selectedMenuName = useSelector(getSelectedMenuName);
  const dispatch = useDispatch();
  const [translate] = useTranslator();
  const theme: Theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const quickAppNavigationItems: NavigationItem[] = useNavigationItems(
    false,
    true
  );

  const serializedNavItems = JSON.stringify(quickAppNavigationItems.map(i => i.menuName));

  const [leftNavMenus, setLeftNavMenus] = React.useState(quickAppNavigationItems);

  React.useEffect(() => {
    const orderedNavigationList: NavigationItem[] = [];
    quickAppNavigationItemsLS.forEach((item: NavigationItem) => {
      const menuItem = quickAppNavigationItems.find(
        (menu) => menu.menuName === item.menuName
      );
      menuItem && orderedNavigationList.push(menuItem);
    });
    if (orderedNavigationList.length > 0) {
      setLeftNavMenus(orderedNavigationList);
    } else {
      setLeftNavMenus(quickAppNavigationItems);
    }
  }, [quickAppNavigationItemsLS, isSmView, serializedNavItems]);

  /**
   * @example getNavLabel(Navigation.CONTACTHISTORY)
   */
  const getNavLabel = (menuName: Navigation): string => {
    const labelMap: Record<string, string> = {
      [Navigation.CONTACTHISTORY]: 'Contact History',
      [Navigation.INTERACTIONSEARCH]: 'Search',
      [Navigation.QUEUE]: 'Queue',
      [Navigation.DIRECTORY]: 'Directory',
      [Navigation.CALENDER]: 'Schedule',
      [Navigation.WEM]: 'WEM',
      [Navigation.SETTINGS]: 'Settings',
      [Navigation.REPORTS]: 'Reporting',
      [Navigation.CUSTOMWORKSPACE]: 'Workspace',
      [Navigation.AGENT_MESSAGES]: 'Messages',
      [Navigation.ENLIGHTEN]: 'CoPilot',
      [Navigation.QUICK_REPLY]: 'Replies',
      [Navigation.SENTIMENT]: 'Sentiment',
      [Navigation.CUSTOMERCARD]: 'Customer',
    };
    return labelMap[menuName] || menuName;
  };

  return (
    <>
      <nav className={styles.lyraSidebar}>
        <div className={styles.lyraNavList}>
          {leftNavMenus.map(({ menuName, icon, tooltip, onClick, showBadge }: NavigationItem) => {
            const isActive = selectedMenuName === menuName;
            return (
              <Tooltip key={menuName} title={tooltip || getNavLabel(menuName)} placement="right" arrow>
                <button
                  type="button"
                  aria-label={tooltip || getNavLabel(menuName)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`${styles.lyraNavItem} ${isActive ? styles.lyraNavItemActive : ''}`}
                  onClick={(e) => {
                    if (onClick) {
                      onClick(e);
                    } else {
                      dispatch(setSelectedMenu({ name: menuName }));
                    }
                  }}
                >
                  {showBadge && inboxCount > 0 && (
                    <span className={styles.lyraNavBadge}>{inboxCount}</span>
                  )}
                  {icon}
                  <span className={styles.lyraNavLabel}>{getNavLabel(menuName)}</span>
                </button>
              </Tooltip>
            );
          })}
        </div>

        <div className={styles.lyraNavBottom}>
          <div className={styles.lyraNavDivider} />
          <Tooltip title={translate('more')} placement="right" arrow>
            <button
              type="button"
              aria-label={translate('more')}
              className={styles.lyraNavItem}
              onClick={() => setIsDrawerOpen(true)}
            >
              <MoreHorizIcon style={{ color: 'rgb(91 103 112)' }} />
              <span className={styles.lyraNavLabel}>More</span>
            </button>
          </Tooltip>
        </div>
      </nav>
      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '250px',
            backgroundColor: '#f3f5f6',
          },
        }}
      >
        <CcfAppHamburgerMenu
          helpUrl="www.google.com"
          isDrawerOpen={isDrawerOpen}
          isAppSpaceMenu={false}
          toggleDrawer={setIsDrawerOpen}
        />
      </Drawer>
    </>
  );
}

export default CcfLyraSidebar;
